import type { BoardDoc } from '../doc/BoardDoc';
import type { Point } from '../doc/geometry';
import type { PaneStore } from './paneStore';
import { screenToWorld } from './viewport/camera';

/** Mounted panes by their root element, so a drag can find the pane under the pointer (split view). */
const panes = new Map<Element, { board: BoardDoc; pane: PaneStore }>();

/** Where the mouse was last seen (client px), so pastes can land at it; null once it has left the page. */
let mouse: Point | null = null;
const trackMouse = (e: PointerEvent) => {
  mouse = { x: e.clientX, y: e.clientY };
};
// no related target: the pointer left the window, a finger lifted or a pen left hover range
const loseMouse = (e: PointerEvent) => {
  if (!e.relatedTarget) mouse = null;
};

export function registerPane(el: Element, entry: { board: BoardDoc; pane: PaneStore }): () => void {
  if (!panes.size) {
    window.addEventListener('pointermove', trackMouse, { passive: true });
    window.addEventListener('pointerout', loseMouse);
  }
  panes.set(el, entry);
  return () => {
    if (panes.get(el) === entry) panes.delete(el);
    if (panes.size) return;
    window.removeEventListener('pointermove', trackMouse);
    window.removeEventListener('pointerout', loseMouse);
    mouse = null;
  };
}

/** Root element of a mounted pane (captions are raised on it). */
export function paneElement(pane: PaneStore): Element | null {
  for (const [el, entry] of panes) if (entry.pane === pane) return el;
  return null;
}

export function paneAt(clientX: number, clientY: number): { el: Element; board: BoardDoc; pane: PaneStore } | null {
  const el = document.elementFromPoint(clientX, clientY)?.closest('.pane');
  if (!el) return null;
  const entry = panes.get(el);
  return entry ? { el, ...entry } : null;
}

/** The pane whose canvas is under the mouse, with the world point there. Null over panels and toolbars. */
export function canvasUnderMouse(): { board: BoardDoc; pane: PaneStore; world: Point } | null {
  if (!mouse) return null;
  const viewport = document.elementFromPoint(mouse.x, mouse.y)?.closest('.viewport');
  const el = viewport?.closest('.pane');
  const entry = el ? panes.get(el) : undefined;
  if (!viewport || !entry) return null;
  const r = viewport.getBoundingClientRect();
  const local = { x: mouse.x - r.left, y: mouse.y - r.top };
  return { ...entry, world: screenToWorld(entry.pane.getState().camera, local) };
}
